import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useUnauthorizedUserThemeContext } from 'app/auth/components/UnauthorizedUserThemeContextProvider'
import { useHistory } from 'react-router-dom'
import { urlStandaloneStepAddWholesaler } from 'app/onboarding/standaloneSteps/routes'
import { useOnboardingContext } from '../../use-onboarding-context'
import { Loader } from '@carsys/ui'
import { useGetOnboardingSupportContactInfo } from './use-get-onboarding-support-contact'

const AlreadyRegisteredWrapper: React.FC = ({ children }) => {
  const { t } = useTranslation()
  const { applicationName: themeName } = useUnauthorizedUserThemeContext()
  const h = useHistory()
  const { onboardingInfo } = useOnboardingContext()
  const { supportEmail, supportPhoneNumber, isLoading } = useGetOnboardingSupportContactInfo()

  const redirectToAuthURL = useCallback(() => {
    const redirectAfterLogin = onboardingInfo?.wholesaler?.UUID
      ? urlStandaloneStepAddWholesaler('/onboarding', onboardingInfo?.wholesaler?.UUID)
      : ''
    const baseURL = '/auth/login'
    // build a redirect url to send user to the in-app add order account screen
    h.push(`${baseURL}${redirectAfterLogin && `?redirect=${redirectAfterLogin}`}`)
  }, [h.push, onboardingInfo?.wholesaler?.UUID])

  const redirectToForgotPassword = useCallback(() => h.push('/auth/password-forgot'), [h.push])

  return (
    <Box textAlign='left'>
      <Box mb={3}>
        <Box mb={2}>
          <Typography variant='h4'> {t('Auth.OnboardingTitle')}</Typography>
        </Box>
        <Box>
          <Typography variant='subtitle1'>{children}</Typography>
        </Box>
      </Box>

      <Box my={2}>
        {isLoading ? (
          <Loader loading />
        ) : (
          <>
            <Box my={1} display='flex' gap='.5rem'>
              <Typography sx={{ fontWeight: 'bold' }}>{t('Auth.Onboarding.CustomerSupport')}</Typography>
            </Box>
            {supportEmail && (
              <Box my={1} display='flex' gap='.5rem'>
                <Typography sx={{ fontWeight: 'bold' }}>{t('Auth.Onboarding.DuplicateContactSupportEmail')}</Typography>
                <Typography>{supportEmail}</Typography>
              </Box>
            )}
            {supportPhoneNumber && (
              <Box my={1} display='flex' gap='.5rem'>
                <Typography sx={{ fontWeight: 'bold' }}>{t('Auth.Onboarding.DuplicateContactPhone')}</Typography>
                <Typography>{supportPhoneNumber}</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box my={2}>
        <Typography component='div' variant='body1'>
          {t('Auth.AlreadyRegisteredMessageMoreDetails')}
        </Typography>
      </Box>

      <Stack direction='row' spacing={2} justifyContent='flex-end'>
        {isLoading ? (
          <Loader loading />
        ) : (
          <>
            {supportEmail && (
              <Button href={`mailto:${supportEmail}`} variant='outlined' color='primary' fullWidth={false}>
                {t('global.contact_support')}
              </Button>
            )}
            <Button variant='outlined' color='primary' fullWidth={false} onClick={redirectToForgotPassword}>
              {t('Auth.BtnForgotPassword')}
            </Button>
            <Button variant='contained' color='primary' fullWidth={false} onClick={redirectToAuthURL}>
              {t('global.go_to_specific_login_page', { environmentName: themeName })}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  )
}

export default AlreadyRegisteredWrapper
